/**
 * React hook for registering animation callbacks with the AnimationOrchestrator.
 * Provides a clean API for using the centralized animation loop in React components.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { animationOrchestrator } from "../managers/AnimationOrchestrator";

export interface UseAnimationFrameOptions {
    "maxFps"?:   number;
    "enabled"?:  boolean;
    "trackFps"?: boolean;
}

export interface UseAnimationFrameReturn { "fps": number }

/**
 * Hook to register an animation callback with the centralized AnimationOrchestrator
 * @param id - Unique identifier for this animation callback
 * @param callback - Function called each frame (receives deltaTime in ms and timestamp)
 * @param options - Configuration options (maxFps, enabled, trackFps)
 * @returns Object containing current FPS if trackFps is enabled
 */
export const useAnimationFrame = (
    id: string,
    callback: ( deltaTime: number, timestamp: number ) => void,
    options: UseAnimationFrameOptions = {},
): UseAnimationFrameReturn => {
    const { maxFps = 60, enabled = true, trackFps = false } = options;

    const [ fps, setFps ] = useState( 0 );

    // Keep a ref to the latest callback to avoid re-registering on every render
    const callbackRef = useRef( callback );

    // Update the ref after render (not during render)
    useLayoutEffect( () => {
        callbackRef.current = callback;
    } );

    // FPS update handler (memoized to avoid re-registrations)
    const handleFpsUpdate = useCallback( ( newFps: number ) => {
        setFps( newFps );
    }, [] );

    useEffect( () => {
        if ( !enabled ) {
            // Ensure callback is unregistered when disabled
            if ( animationOrchestrator.isRegistered( id ) ) {
                animationOrchestrator.unregister( id );
            }

            return;
        }

        // Wrapper that calls the latest callback ref
        const wrappedCallback = ( deltaTime: number, timestamp: number ): void => {
            callbackRef.current( deltaTime, timestamp );
        };

        // Register with optional FPS tracking
        animationOrchestrator.register( id, wrappedCallback, maxFps, trackFps ? handleFpsUpdate : null );

        return () => {
            animationOrchestrator.unregister( id );
        };
    }, [ id, maxFps, enabled, trackFps, handleFpsUpdate ] );

    return { fps };
};
